/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : detail component
 *   Integrations : Goal details API
 *   Version : 1.0.1
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { Chip, EmptyScreen, Loader } from "../../Components/Other";
import { useParams } from "react-router-dom";
import { DataTableComponent } from "../../Components/DataTable";
import { PrimaryButton } from "../../Components/Buttons";
import Icon from "../../Components/icons";
import { TransactionModal } from "../../Components/Modals";
import { APIService } from "../../Service";

export default function OrderDetail() {
  //Redux title and searchbar setting
  const dispatch = useDispatch();
  const [order, setOrder] = useState();
  const [orderType, setOrderType] = useState();
  const { type, id } = useParams();
  const [modal, setModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [orderDetails, setOrderDetails] = useState([]);
  const [timeline, setTimeline] = useState([]);

  // API sends null or "N/A" when no image is available
  const _hasImage = (src) => Boolean(src) && src !== "N/A";

  const COLUMNS = [
    {
      name: "Funds",
      selector: (row) => row.mutual_fund,
      width: "250px",
      cell: (row) => (
        <div className="d-flex align-items-center column-gap-2">
          {_hasImage(row.image) && (
            <img
              src={row.image}
              alt={row.mutual_fund}
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                objectFit: "cover",
              }}
            />
          )}
          <span className="fw-500">{row.mutual_fund}</span>
        </div>
      ),
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
    },
    {
      name: "Unit",
      selector: (row) => row.units,
    },
    {
      name: "NAV",
      selector: (row) => row.allocated_nav,
    },
    {
      name: "Allocated Date",
      width: "140px",
      selector: (row) => row.allocated_date,
    },
    {
      name: "Folio No",
      selector: (row) => row.folio_number,
    },
    {
      name: "Transaction ID",
      width: "130px",
      selector: (row) => row.id,
      cell: (row) => `TXN${row.id}`,
    },
    {
      name: "Action",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
          name="View detail"
          onPress={() => {
            setModal(row.timeline);
          }}
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  useEffect(() => {
    if (id) {
      _getOrderDetail(id);
    }
    dispatch(setNavLink(["/orders"]));
    dispatch(setSearchEnabled(true));
  }, [id]);

  const _getOrderDetail = (orderId) => {
    setLoader(true);

    const payload = {
      order_id: orderId,
    };

    APIService(true, "orders/detail", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;
          const summary = data.summary;
          console.log(data);

          setOrder({
            orderType: summary.investment_type,
            orderId: summary.display_order_id,
            date: summary.created_at_on,
            amount: summary.total_amount,
            paymentStatus: summary.payment_status,
            orderStatus: summary.order_status,
            goal: summary.order_name,
            orderImage: summary.order_image,
            progress: summary.progress,
            type: summary.type,
            userName: summary.user_name,
          });

          // Set order type from summary
          setOrderType(type || "goal");

          // Set order details for table
          setOrderDetails(data.order_details || []);

          // Build timeline from first order detail if available
          if (data.order_details && data.order_details.length > 0) {
            setTimeline(data.order_details[0].timeline);
          }

          // Set page title
          dispatch(
            setPageTitle([
              "Orders",
              summary.display_order_id || "Order Detail",
            ]),
          );

          setIsEmpty(false);
        } else {
          setIsEmpty(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  if (loader) {
    return <Loader />;
  }

  if (isEmpty) {
    return <EmptyScreen message={"No records to be shown"} />;
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card bg-transparent rounded-24 d-flex flex-column">
          <div className="d-flex p-24">
            <div className="e-glass-card rounded-16 w-100 p-24 d-flex flex-column">
              <div className="d-flex align-items-center column-gap-3">
                {_hasImage(order?.orderImage) && (
                  <div className="bg-white p-1 rounded-12 e-border border-platinum-gray">
                    <img src={order.orderImage} alt="order image" width={32} />
                  </div>
                )}
                <h3 className="mb-0 fw-500 fs-18 lh-20">{order?.goal}</h3>
                <Chip
                  text={order?.orderType}
                  labelStyle={"fw-400 fs-16 lh-24 px-1"}
                  style="e-border border-facebook-blue px-1  bg-lavender-blue bg-opacity-43"
                  textClr="text-eerie-black"
                />
              </div>

              <div className="d-flex flex-wrap gap-sm-4 gap-3 justify-content-md-between  pt-24">
                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Order Type
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {order?.orderType}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Order ID
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 text-truncate">
                      {order?.orderId}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Date
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {order?.date}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Amount
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {order?.amount}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Payment Status
                    </h4>
                    <p
                      className={`fw-400 fs-18 lh-24 m-0 ${
                        order?.paymentStatus === "Success"
                          ? "text-medium-sea-green"
                          : "text-copper-red"
                      }`}
                    >
                      {order?.paymentStatus}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-column"
                  style={{ width: "max-content" }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Order Status
                    </h4>
                    <p
                      className={`fw-400 fs-18 lh-24 m-0 ${
                        order?.orderStatus?.toLowerCase().startsWith("success")
                          ? "text-medium-sea-green"
                          : "text-copper-red"
                      }`}
                    >
                      {order?.orderStatus}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {orderType?.toLowerCase() === "goal" && (
            <DataTableComponent
              info={true}
              columns={COLUMNS}
              data={orderDetails}
              pagination={false}
              className={"e-table-basic"}
            />
          )}
          {orderType?.toLowerCase() !== "goal" && timeline.length > 0 && (
            <div className="p-24 position-relative">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-600 fs-20 m-0">Transaction status</h3>
              </div>

              {/* Timeline */}
              <div className="d-flex flex-column gap-3">
                {timeline.map((step, index) => (
                  <div key={index} className="d-flex gap-3 align-items-start">
                    {/* Left side icon + dotted line */}
                    <div
                      className="position-relative"
                      style={{ width: "24px" }}
                    >
                      {/* Green Tick */}
                      <div className="text-black">
                        <Icon icon="check-mark" size={20} />
                      </div>

                      {/* Dotted Line */}
                      {index !== timeline.length - 1 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "22px",
                            left: "9px",
                            height: "38px",
                            borderLeft: "2px dotted #8C8C8C",
                          }}
                        ></div>
                      )}
                    </div>

                    {/* Text */}
                    <div>
                      <div className="fw-500 fs-16">{step.label}</div>
                      <div className="d-flex column-gap-2">
                        <div className="fs-14 text-muted">{step.value}</div>
                        <div className="fs-14 text-muted">, {step.status}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <TransactionModal
        open={modal}
        onClose={() => {
          setModal(false);
        }}
        data={modal}
      />
    </div>
  );
}
